import { useState, useEffect } from 'react';
import { supabase, testSupabaseConnectivity } from '../lib/supabase';
import type { AuthState } from '../types';

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    // Check initial session
    const initAuth = async () => {
      try {
        await checkInitialSession();
      } catch (error) {
        console.error('🔴 AUTH: Failed to initialize auth:', error);
        // Ensure we never stay in loading state
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };
    
    initAuth();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          console.log('🔵 Auth state changed:', event, session?.user?.email || 'no user');
          
          // Handle sign out events
          if (event === 'SIGNED_OUT') {
            console.log('🔄 User signed out, clearing auth state...');
            setAuthState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }
          
          if (session?.user) {
            setAuthState({
              user: {
                id: session.user.id,
                email: session.user.email || '',
                name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
              },
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            setAuthState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('🔴 AUTH: Error handling auth state change:', error);
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkInitialSession = async () => {
    try {
      
      // Enhanced session check with better error handling
      let sessionResult;
      let sessionError = null;
      
      try {
        sessionResult = await supabase.auth.getSession();
        sessionError = sessionResult.error;
      } catch (err) {
        sessionError = err;
      }
      
      // Handle refresh token errors gracefully
      if (sessionError && (
        sessionError.message?.includes('refresh_token_not_found') ||
        sessionError.message?.includes('Invalid Refresh Token') ||
        sessionError.message?.includes('Refresh Token Not Found') ||
        sessionError.code === 'refresh_token_not_found'
      )) {
        await clearStaleAuthData();
        // Set clean state after clearing
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }
      
      // Handle other session errors
      if (sessionError) {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }
      
      // Extract session from result
      const session = sessionResult?.data?.session;
      
      
      if (session?.user) {
        setAuthState({
          user: {
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
          },
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('🔴 AUTH: Session check error:', error);
      
      // Always clear auth state on critical errors to prevent blank screen
      const errorMessage = error && typeof error === 'object' && 'message' in error ? String((error as any).message) : '';
      
      if (errorMessage.includes('refresh_token') || 
          errorMessage.includes('Refresh Token') ||
          errorMessage.includes('Invalid') ||
          errorMessage.includes('session')) {
        await clearStaleAuthData();
      }
      
      // Always set a clean, non-loading state to prevent blank screen
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  };

  // Enhanced function to clear stale auth data
  const clearStaleAuthData = async () => {
    try {
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear all possible localStorage keys related to Supabase auth
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('supabase') || 
          key.includes('sb-') || 
          key.includes('promptos-auth') ||
          key.includes('auth-token')
        )) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn('Could not remove key:', key, e);
        }
      });
      
      console.log('✅ AUTH: Stale auth data cleared successfully');
    } catch (error) {
      console.error('🔴 AUTH: Error clearing stale auth data:', error);
    }
  };

  // Force clear any stale auth data on mount
  useEffect(() => {
    const clearStaleAuthData = async () => {
      try {
        // Check if there's existing auth data that might be stale
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          await supabase.auth.signOut();
        }
      } catch (e) {
        console.warn('🔄 AUTH: Could not check for stale auth data:', e);
        // If we can't even check, try to clear
        try {
          await supabase.auth.signOut();
        } catch (clearError) {
          console.warn('Could not clear auth on error:', clearError);
        }
      }
    };
    
    clearStaleAuthData();
  }, []);
  
  const login = async (email: string, password: string) => {
    try {
      
      // Clear any existing session first to avoid conflicts
      try {
        await supabase.auth.signOut();
      } catch (clearError) {
      }
      
      
      // Check environment variables at login time
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });


      if (error) {
        console.error('Supabase Login Error:', error);
        return { success: false, error: `Supabase Auth Error: ${error.message} (Code: ${error.code || 'unknown'})` };
      }

      if (!data?.user) {
        return { success: false, error: 'No user data returned from authentication' };
      }

      return { success: true };
    } catch (err) {
      console.error('Auth: Unexpected error during login:', err);
      return { success: false, error: `Login exception: ${err instanceof Error ? err.message : 'Unknown error'}` };
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password: password,
        options: {
          data: { name: name.trim() },
          emailRedirectTo: `${window.location.origin}/confirm`,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user && !data.session) {
        return { 
          success: true, 
          message: 'Account created! Please check your email and click the confirmation link.'
        };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: 'Signup failed' };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Logout failed' };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        message: 'Password reset email sent! Check your inbox.'
      };
    } catch (err) {
      return { success: false, error: 'Password reset failed' };
    }
  };

  const confirmEmail = async (token: string, type: string) => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: type as any,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: 'Email confirmation failed' };
    }
  };

  return {
    ...authState,
    login,
    signup,
    logout,
    resetPassword,
    confirmEmail,
  };
}